/* eslint-disable max-lines-per-function */
import {
  Avatar,
  AvatarFallback,
  Button,
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  Input,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  CommandEmpty,
  FieldError,
} from '@e2n/ui';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { cn } from '@/lib/utils';
import { faAnglesUpDown, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAbteilungsleiter } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useAbteilungsleiter/useAbteilungsleiter';
import { Abteilungsleiter } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abteilungsleiter';

export function AbteilungsleiterSelect({
  onChange,
  selectedArbeitsbereichId,
  selectedAbteilungsleiter,
  showWarning = false,
}: {
  onChange: (abteilungsleiter: Abteilungsleiter) => void;
  selectedArbeitsbereichId: string | undefined;
  selectedAbteilungsleiter: Abteilungsleiter;
  showWarning?: boolean;
}) {
  const { standorte } = useStandortContext();
  const standortIds = standorte.map((s) => s.id);

  if (!selectedArbeitsbereichId) {
    return (
      <AbteilungsleiterSelectContent
        showWarning={showWarning}
        abteilungsleiter={[]}
        onChange={onChange}
        selectedAbteilungsleiter={selectedAbteilungsleiter}
      />
    );
  }

  return (
    <AbteilungsleiterSelectWithData
      showWarning={showWarning}
      onChange={onChange}
      selectedArbeitsbereichId={selectedArbeitsbereichId}
      standortIds={standortIds}
      selectedAbteilungsleiter={selectedAbteilungsleiter}
    />
  );
}

function AbteilungsleiterSelectWithData({
  onChange,
  selectedArbeitsbereichId,
  standortIds,
  selectedAbteilungsleiter,
  showWarning,
}: {
  onChange: (abteilungsleiter: Abteilungsleiter) => void;
  selectedArbeitsbereichId: string;
  standortIds: string[];
  selectedAbteilungsleiter: Abteilungsleiter;
  showWarning: boolean;
}) {
  const { abteilungsleiter } = useAbteilungsleiter(
    standortIds,
    selectedArbeitsbereichId,
  );

  return (
    <AbteilungsleiterSelectContent
      abteilungsleiter={abteilungsleiter}
      onChange={onChange}
      selectedAbteilungsleiter={selectedAbteilungsleiter}
      showWarning={showWarning}
    />
  );
}

function AbteilungsleiterSelectContent({
  onChange,
  abteilungsleiter,
  selectedAbteilungsleiter,
  showWarning,
}: {
  onChange: (abteilungsleiter: Abteilungsleiter) => void;
  abteilungsleiter: Abteilungsleiter[];
  selectedAbteilungsleiter: Abteilungsleiter;
  showWarning: boolean;
}) {
  const { t } = useTranslation();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const hasItems = abteilungsleiter.length > 0;

  function handleSelectAbteilungsleiter(value: string) {
    const selectedAbteilungsleiter = abteilungsleiter.find(
      (a) => a.id === value,
    );
    onChange(selectedAbteilungsleiter);
  }

  function onChangeFilter(value: string) {
    setFilter(value);
  }

  return (
    <div>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal>
        <PopoverTrigger className="hidden md:flex" asChild>
          <Trigger
            showWarning={showWarning}
            selectedAbteilungsleiter={selectedAbteilungsleiter}
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={`bg-white p-2 px-0 w-108 overflow-auto`}>
          <Command>
            <div className="w-full">
              <CommandInput
                placeholder={t(
                  'domain.mitarbeiter.rotationsplan.filter.suchen',
                )}
              />
            </div>
            <CommandList>
              <CommandEmpty className="py-1 text-neutrals-700">
                {hasItems
                  ? t('domain.mitarbeiter.rotationsplan.filter.keineErgebnisse')
                  : t(
                      'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.notFound',
                    )}
              </CommandEmpty>
              {abteilungsleiter.map((abteilungsleiter) => (
                <CommandItem
                  key={abteilungsleiter.id}
                  value={abteilungsleiter.getName() + '%' + abteilungsleiter.id}
                  onSelect={() => {
                    handleSelectAbteilungsleiter(abteilungsleiter.id);
                    setIsPopoverOpen(false);
                  }}>
                  <ListItem
                    abteilungsleiter={abteilungsleiter}
                    selectedAbteilungsleiter={selectedAbteilungsleiter}
                  />
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerTrigger className="flex md:hidden" asChild>
          <Trigger
            showWarning={showWarning}
            selectedAbteilungsleiter={selectedAbteilungsleiter}
          />
        </DrawerTrigger>
        <DrawerContent className="flex flex-col gap-4 p-4">
          <div className="font-semibold text-center">
            {t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.label',
            )}
          </div>
          <Input
            className="grow"
            defaultValue={filter}
            placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
            onChange={(event) => onChangeFilter(event.target.value)}
          />
          <ScrollArea className="overflow-auto">
            <div className="flex flex-col gap-2">
              {abteilungsleiter.length > 0 ? (
                abteilungsleiter
                  .filter((m) =>
                    m
                      .getName()
                      .toLocaleLowerCase()
                      .includes(filter.toLocaleLowerCase()),
                  )
                  .map((m, index) => (
                    <div
                      className="flex flex-col gap-2"
                      key={m.id}
                      onClick={() => {
                        handleSelectAbteilungsleiter(m.id);
                        setIsDrawerOpen(false);
                      }}>
                      <ListItem
                        abteilungsleiter={m}
                        selectedAbteilungsleiter={selectedAbteilungsleiter}
                      />
                      {index < abteilungsleiter.length - 1 && (
                        <div className="w-full h-px bg-neutrals-200"></div>
                      )}
                    </div>
                  ))
              ) : (
                <div className="text-sm text-center text-neutrals-700">
                  {hasItems
                    ? t(
                        'domain.mitarbeiter.rotationsplan.filter.keineErgebnisse',
                      )
                    : t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.notFound',
                      )}
                </div>
              )}
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function Trigger({
  selectedAbteilungsleiter,
  showWarning,
  ...props
}: {
  selectedAbteilungsleiter: Abteilungsleiter;
  showWarning: boolean;
} & HTMLAttributes<HTMLButtonElement>) {
  const { t } = useTranslation();

  return (
    <div className={cn('flex flex-col gap-1', props.className)}>
      <Button
        {...props}
        variant="outline-secondary"
        role="combobox"
        className={cn(
          'w-full justify-between font-normal  [&>svg]:text-neutrals-900',
          selectedAbteilungsleiter ? 'text-neutrals-900' : 'text-neutrals-700',
        )}>
        {selectedAbteilungsleiter
          ? selectedAbteilungsleiter.getName()
          : t('actions.auswaehlen')}
        <FontAwesomeIcon icon={faAnglesUpDown} />
      </Button>
      {showWarning && (
        <FieldError
          variant="warning"
          errors={[
            {
              message: t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.reset',
              ),
            },
          ]}
        />
      )}
    </div>
  );
}

function ListItem({
  abteilungsleiter,
  selectedAbteilungsleiter,
}: {
  abteilungsleiter: Abteilungsleiter;
  selectedAbteilungsleiter?: Abteilungsleiter;
}) {
  return (
    <div className="flex items-center w-full gap-2">
      <Avatar>
        <AvatarFallback>{abteilungsleiter.getInitials()}</AvatarFallback>
      </Avatar>
      <div className="flex items-center justify-between w-full">
        <div className="text-sm truncate whitespace-nowrap">
          {abteilungsleiter.getName()}
        </div>
        {abteilungsleiter.id === selectedAbteilungsleiter?.id && (
          <FontAwesomeIcon className="text-primary-blue-600" icon={faCheck} />
        )}
      </div>
    </div>
  );
}
